import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import Avatar from 'components/Avatar';
import CommentMentionTextarea from 'components/CommentMentionTextarea';
import { Icon } from 'utils/icons';

const VIEWPORT_MARGIN = 12;
const POPOVER_GAP = 8;
const ACTIONS_MENU_WIDTH = 112;
const ACTIONS_MENU_HEIGHT = 96;

const formatTimestamp = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  return `${dateLabel} • ${timeLabel}`;
};

const CommentAvatar = ({ name, image, className = '' }) => (
  <Avatar
    name={name}
    className={`cell-comments-popover__avatar ${className}`}
    image={image ? <img src={image} alt="" /> : null}
  />
);

CommentAvatar.propTypes = {
  name: PropTypes.string,
  image: PropTypes.string,
  className: PropTypes.string,
};

CommentAvatar.defaultProps = {
  name: '',
  image: null,
  className: '',
};

const CellCommentsPopover = ({
  anchorElement,
  anchorRect,
  comments,
  currentUser,
  isLoading,
  isSubmitting,
  mentionableUsers,
  onClose,
  onCreate,
  onDelete,
  onUpdate,
}) => {
  const popoverRef = useRef(null);
  const composerRef = useRef(null);
  const editRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const actionsMenuAnchorRef = useRef(null);
  const [position, setPosition] = useState({
    left: VIEWPORT_MARGIN,
    top: VIEWPORT_MARGIN,
  });
  const [draft, setDraft] = useState('');
  const [draftMentionIds, setDraftMentionIds] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [editingMentionIds, setEditingMentionIds] = useState([]);
  const [menuCommentId, setMenuCommentId] = useState(null);
  const [actionsMenuPosition, setActionsMenuPosition] = useState(null);
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const commentTitle = comments.length
    ? `Comments (${comments.length})`
    : 'Comments';

  const updatePosition = useCallback(() => {
    const popover = popoverRef.current;
    const currentAnchorRect =
      anchorElement?.getBoundingClientRect?.() || anchorRect;
    if (!popover || !currentAnchorRect || typeof window === 'undefined') return;

    const popoverRect = popover.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const maxLeft = Math.max(
      VIEWPORT_MARGIN,
      viewportWidth - popoverRect.width - VIEWPORT_MARGIN
    );
    const preferredTop = currentAnchorRect.bottom + POPOVER_GAP;
    const top =
      preferredTop + popoverRect.height <= viewportHeight - VIEWPORT_MARGIN
        ? preferredTop
        : Math.max(
            VIEWPORT_MARGIN,
            currentAnchorRect.top - popoverRect.height - POPOVER_GAP
          );

    setPosition({
      left: Math.min(
        Math.max(currentAnchorRect.left, VIEWPORT_MARGIN),
        maxLeft
      ),
      top,
    });
  }, [anchorElement, anchorRect]);

  const updateActionsMenuPosition = useCallback(() => {
    const anchor = actionsMenuAnchorRef.current;
    if (!anchor || typeof window === 'undefined') return;

    const menuAnchorRect = anchor.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const maxLeft = Math.max(
      VIEWPORT_MARGIN,
      viewportWidth - ACTIONS_MENU_WIDTH - VIEWPORT_MARGIN
    );
    const maxTop = Math.max(
      VIEWPORT_MARGIN,
      viewportHeight - ACTIONS_MENU_HEIGHT - VIEWPORT_MARGIN
    );
    const nextPosition = {
      left: Math.min(
        Math.max(menuAnchorRect.right - ACTIONS_MENU_WIDTH, VIEWPORT_MARGIN),
        maxLeft
      ),
      top: Math.min(menuAnchorRect.bottom + 4, maxTop),
    };

    setActionsMenuPosition(current =>
      current?.left === nextPosition.left && current?.top === nextPosition.top
        ? current
        : nextPosition
    );
  }, []);

  useLayoutEffect(() => {
    updatePosition();
  }, [comments.length, isLoading, updatePosition]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (!menuCommentId) return undefined;

    updateActionsMenuPosition();
    window.addEventListener('resize', updateActionsMenuPosition);
    window.addEventListener('scroll', updateActionsMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateActionsMenuPosition);
      window.removeEventListener('scroll', updateActionsMenuPosition, true);
    };
  }, [menuCommentId, updateActionsMenuPosition]);

  useEffect(() => {
    const handlePointerDown = event => {
      if (
        !popoverRef.current?.contains(event.target) &&
        !actionsMenuRef.current?.contains(event.target) &&
        !event.target.closest?.('.comment-mention-input__suggestions')
      ) {
        onClose();
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const submitDraft = async event => {
    event?.preventDefault();
    if (!draft.trim() || isSubmitting) return;
    const submittedDraft = draft.trim();
    const submittedMentionIds = [...draftMentionIds];
    setDraft('');
    setDraftMentionIds([]);
    try {
      await onCreate(submittedDraft, submittedMentionIds);
      composerRef.current?.focus();
    } catch (error) {
      setDraft(currentDraft => currentDraft || submittedDraft);
      setDraftMentionIds(currentMentionIds =>
        currentMentionIds.length ? currentMentionIds : submittedMentionIds
      );
      // The parent reports the mutation error; restore this draft for retry.
    }
  };

  const handleComposerKeyDown = event => {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      submitDraft(event);
    }
  };

  const saveEdit = async commentId => {
    if (!editingBody.trim() || isSubmitting) return;
    try {
      await onUpdate(commentId, editingBody.trim(), editingMentionIds);
      setEditingCommentId(null);
      setEditingBody('');
      setEditingMentionIds([]);
    } catch (error) {
      // Preserve the edited value so the user can retry.
    }
  };

  const confirmDelete = async commentId => {
    if (isSubmitting) return;
    try {
      await onDelete(commentId);
      setDeleteCommentId(null);
      setMenuCommentId(null);
      setActionsMenuPosition(null);
    } catch (error) {
      // Keep confirmation visible while the parent surfaces the failure.
    }
  };

  const toggleActionsMenu = (commentId, event) => {
    if (menuCommentId === commentId) {
      setMenuCommentId(null);
      setActionsMenuPosition(null);
      actionsMenuAnchorRef.current = null;
      return;
    }

    actionsMenuAnchorRef.current = event.currentTarget;
    setMenuCommentId(commentId);
    updateActionsMenuPosition();
  };

  const menuComment = comments.find(comment => comment.id === menuCommentId);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <section
        ref={popoverRef}
        aria-label={commentTitle}
        className="cell-comments-popover"
        role="dialog"
        style={{ left: `${position.left}px`, top: `${position.top}px` }}
      >
        <header className="cell-comments-popover__header">
          <h2>{commentTitle}</h2>
        </header>

        <div className="cell-comments-popover__thread" aria-live="polite">
          {isLoading && comments.length === 0 ? (
            <div className="cell-comments-popover__empty">
              Loading comments…
            </div>
          ) : null}
          {!isLoading && comments.length === 0 ? (
            <div className="cell-comments-popover__empty">
              Start the conversation.
            </div>
          ) : null}
          {comments.map(comment => {
            const isEditing = editingCommentId === comment.id;
            const isDeleting = deleteCommentId === comment.id;
            const isMenuOpen = menuCommentId === comment.id;
            return (
              <article
                className="cell-comments-popover__comment"
                key={comment.id}
              >
                <CommentAvatar
                  image={comment.author_profile_image}
                  name={comment.author_name || comment.author_email}
                />
                <div className="cell-comments-popover__comment-content">
                  <div className="cell-comments-popover__comment-heading">
                    <strong>
                      {comment.author_name || comment.author_email}
                    </strong>
                    {comment.is_author ? (
                      <div className="cell-comments-popover__comment-actions">
                        <button
                          aria-expanded={isMenuOpen}
                          aria-label="Comment actions"
                          className="cell-comments-popover__more-button"
                          onClick={event =>
                            toggleActionsMenu(comment.id, event)
                          }
                          type="button"
                        >
                          <Icon name="MoreIcon" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {isEditing ? (
                    <div className="cell-comments-popover__edit-form">
                      <CommentMentionTextarea
                        ariaLabel="Edit comment"
                        inputRef={editRef}
                        maxLength={2000}
                        mentionableUsers={mentionableUsers}
                        mentionedUserIds={editingMentionIds}
                        onChange={setEditingBody}
                        onMentionedUserIdsChange={setEditingMentionIds}
                        value={editingBody}
                      />
                      <div>
                        <button
                          onClick={() => {
                            setEditingCommentId(null);
                            setEditingBody('');
                            setEditingMentionIds([]);
                          }}
                          type="button"
                        >
                          Cancel
                        </button>
                        <button
                          disabled={!editingBody.trim() || isSubmitting}
                          onClick={() => saveEdit(comment.id)}
                          type="button"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : isDeleting ? (
                    <div className="cell-comments-popover__delete-confirmation">
                      <span>Delete this comment?</span>
                      <button
                        onClick={() => setDeleteCommentId(null)}
                        type="button"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={isSubmitting}
                        onClick={() => confirmDelete(comment.id)}
                        type="button"
                      >
                        Delete
                      </button>
                    </div>
                  ) : (
                    <>
                      <p>{comment.body}</p>
                      <time dateTime={comment.created_on}>
                        {formatTimestamp(comment.created_on)}
                      </time>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <form
          className="cell-comments-popover__composer"
          onSubmit={submitDraft}
        >
          <CommentAvatar
            image={currentUser?.profile_image}
            name={currentUser?.name || currentUser?.email || 'You'}
          />
          <CommentMentionTextarea
            ariaLabel="Reply"
            inputRef={composerRef}
            maxLength={2000}
            mentionableUsers={mentionableUsers}
            mentionedUserIds={draftMentionIds}
            onChange={setDraft}
            onKeyDown={handleComposerKeyDown}
            onMentionedUserIdsChange={setDraftMentionIds}
            placeholder="Reply"
            value={draft}
          />
          <button
            aria-label="Send comment"
            disabled={!draft.trim() || isSubmitting}
            type="submit"
          >
            <Icon aria-hidden="true" name="ArrowUpIcon" />
          </button>
        </form>
      </section>

      {menuComment && actionsMenuPosition ? (
        <div
          ref={actionsMenuRef}
          className="cell-comments-popover__actions-menu"
          role="menu"
          style={{
            left: `${actionsMenuPosition.left}px`,
            top: `${actionsMenuPosition.top}px`,
          }}
        >
          <button
            onClick={() => {
              setEditingCommentId(menuComment.id);
              setEditingBody(menuComment.body);
              setEditingMentionIds(
                (menuComment.mentioned_users || []).map(user => user.id)
              );
              setMenuCommentId(null);
              setActionsMenuPosition(null);
            }}
            role="menuitem"
            type="button"
          >
            Edit <Icon name="Edit" />
          </button>
          <button
            onClick={() => {
              setDeleteCommentId(menuComment.id);
              setMenuCommentId(null);
              setActionsMenuPosition(null);
            }}
            role="menuitem"
            type="button"
          >
            Delete <Icon name="DeleteOutlineIcon" />
          </button>
        </div>
      ) : null}
    </>,
    document.body
  );
};

CellCommentsPopover.propTypes = {
  anchorElement: PropTypes.instanceOf(Element),
  anchorRect: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    top: PropTypes.number,
  }).isRequired,
  comments: PropTypes.array,
  currentUser: PropTypes.object,
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  mentionableUsers: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

CellCommentsPopover.defaultProps = {
  anchorElement: null,
  comments: [],
  currentUser: null,
  isLoading: false,
  isSubmitting: false,
  mentionableUsers: [],
};

export default CellCommentsPopover;
